/* ============================================================
   bubbles.js — bulles de savon flottantes (pages secondaires)
   Exposes window.BubblesFX. Vanilla, uses global THREE (r128).
   Usage : BubblesFX.create(canvas, { accent, count })
   ============================================================ */
(function () {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    window.BubblesFX = { create: () => ({}) };
    return;
  }

  const SNOISE = `
    vec3 mod289(vec3 x){return x-floor(x*(1.0/289.0))*289.0;}
    vec4 mod289(vec4 x){return x-floor(x*(1.0/289.0))*289.0;}
    vec4 permute(vec4 x){return mod289(((x*34.0)+1.0)*x);}
    vec4 taylorInvSqrt(vec4 r){return 1.79284291400159-0.85373472095314*r;}
    float snoise(vec3 v){
      const vec2 C=vec2(1.0/6.0,1.0/3.0);const vec4 D=vec4(0.0,0.5,1.0,2.0);
      vec3 i=floor(v+dot(v,C.yyy));vec3 x0=v-i+dot(i,C.xxx);
      vec3 g=step(x0.yzx,x0.xyz);vec3 l=1.0-g;vec3 i1=min(g.xyz,l.zxy);vec3 i2=max(g.xyz,l.zxy);
      vec3 x1=x0-i1+C.xxx;vec3 x2=x0-i2+C.yyy;vec3 x3=x0-D.yyy;
      i=mod289(i);
      vec4 p=permute(permute(permute(i.z+vec4(0.0,i1.z,i2.z,1.0))+i.y+vec4(0.0,i1.y,i2.y,1.0))+i.x+vec4(0.0,i1.x,i2.x,1.0));
      float n_=0.142857142857;vec3 ns=n_*D.wyz-D.xzx;
      vec4 j=p-49.0*floor(p*ns.z*ns.z);
      vec4 x_=floor(j*ns.z);vec4 y_=floor(j-7.0*x_);
      vec4 x=x_*ns.x+ns.yyyy;vec4 y=y_*ns.x+ns.yyyy;vec4 h=1.0-abs(x)-abs(y);
      vec4 b0=vec4(x.xy,y.xy);vec4 b1=vec4(x.zw,y.zw);
      vec4 s0=floor(b0)*2.0+1.0;vec4 s1=floor(b1)*2.0+1.0;vec4 sh=-step(h,vec4(0.0));
      vec4 a0=b0.xzyw+s0.xzyw*sh.xxyy;vec4 a1=b1.xzyw+s1.xzyw*sh.zzww;
      vec3 p0=vec3(a0.xy,h.x);vec3 p1=vec3(a0.zw,h.y);vec3 p2=vec3(a1.xy,h.z);vec3 p3=vec3(a1.zw,h.w);
      vec4 norm=taylorInvSqrt(vec4(dot(p0,p0),dot(p1,p1),dot(p2,p2),dot(p3,p3)));
      p0*=norm.x;p1*=norm.y;p2*=norm.z;p3*=norm.w;
      vec4 m=max(0.6-vec4(dot(x0,x0),dot(x1,x1),dot(x2,x2),dot(x3,x3)),0.0);m=m*m;
      return 42.0*dot(m*m,vec4(dot(p0,x0),dot(p1,x1),dot(p2,x2),dot(p3,x3)));
    }`;

  function createBubbles(canvas, opts) {
    if (!window.THREE) { console.warn('BubblesFX: THREE not found'); return {}; }
    opts = opts || {};

    const accent = new THREE.Color(opts.accent || '#1745FF');
    const COUNT  = opts.count !== undefined ? Math.max(1, parseInt(opts.count)) : 14;
    const Z_PLANE = 2.35;

    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    const scene  = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 100);
    camera.position.z = 7;

    function resize() {
      const w = canvas.clientWidth  || window.innerWidth;
      const h = canvas.clientHeight || window.innerHeight;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    }
    resize();
    window.addEventListener('resize', resize, { passive: true });

    function worldBounds() {
      const dist  = camera.position.z - Z_PLANE;
      const halfH = Math.tan((camera.fov * Math.PI) / 360) * dist;
      return { halfW: halfH * camera.aspect, halfH };
    }

    // Bulle de savon : centre transparent, bord iridescent
    const bubbleMat = new THREE.ShaderMaterial({
      transparent: true, depthWrite: false, depthTest: false,
      uniforms: {
        uTime:    { value: 0 },
        uOpacity: { value: 1.0 },
        uTint:    { value: accent.clone() },
      },
      vertexShader: `
        varying vec3 vN; varying vec3 vV;
        void main(){
          vec4 wp = modelMatrix * vec4(position, 1.0);
          vN = normalize(mat3(modelMatrix) * normal);
          vV = normalize(cameraPosition - wp.xyz);
          gl_Position = projectionMatrix * viewMatrix * wp;
        }`,
      fragmentShader: SNOISE + `
        precision highp float;
        varying vec3 vN; varying vec3 vV;
        uniform float uTime; uniform float uOpacity; uniform vec3 uTint;
        vec3 spectral(float t){
          float s = fract(t * 0.50);
          vec3 c0 = vec3(0.047, 0.137, 0.502); // #0C2380
          vec3 c1 = vec3(0.090, 0.271, 1.000); // #1745FF
          vec3 c2 = vec3(0.380, 0.506, 1.000); // #6181FF
          vec3 c3 = vec3(0.078, 1.000, 0.969); // #14FFF7
          vec3 c4 = vec3(1.000, 0.235, 0.776); // #FF3CC7
          float f  = s * 5.0;
          float ft = smoothstep(0.0, 1.0, fract(f));
          if(f < 1.0) return mix(c0, c1, ft);
          if(f < 2.0) return mix(c1, c2, ft);
          if(f < 3.0) return mix(c2, c3, ft);
          if(f < 4.0) return mix(c3, c4, ft);
          return mix(c4, c0, ft);
        }
        void main(){
          vec3 N = normalize(vN); vec3 V = normalize(vV);
          float ndv  = max(dot(N, V), 0.0);
          float fres = pow(1.0 - ndv, 2.3);
          vec3 irid  = spectral((1.0 - ndv) * 2.0 + N.y * 1.8 + uTime * 0.12);
          float lum  = dot(irid, vec3(0.213, 0.715, 0.072));
          irid = clamp(mix(vec3(lum), irid, 2.2), 0.0, 1.0);
          vec3 col = mix(vec3(1.0), irid, pow(fres, 0.6) * 0.95);
          vec3 L1  = normalize(vec3(-0.5, 0.72, 0.55));
          vec3 L2  = normalize(vec3(0.42, 0.5,  0.8));
          float rl1  = max(dot(reflect(-L1, N), V), 0.0);
          float broad = pow(rl1, 22.0);
          float core  = pow(rl1, 90.0);
          float glint = pow(max(dot(reflect(-L2, N), V), 0.0), 300.0);
          col  += broad * 0.18 + core * 0.85 + glint * 1.0;
          float rim   = pow(fres, 2.2);
          float alpha = rim * 0.88 + broad * 0.06 + core * 0.55 + glint * 0.7;
          gl_FragColor = vec4(col, clamp(alpha, 0.0, 1.0) * uOpacity);
        }`,
    });

    const bubbleGeo = new THREE.SphereGeometry(1, 48, 48);
    const group = new THREE.Group();
    scene.add(group);

    function spawnData(t, staggerIdx) {
      const big  = Math.random() < 0.25;
      const size = big ? (0.14 + Math.random() * 0.12) : (0.04 + Math.random() * 0.09);
      const { halfW, halfH } = worldBounds();
      const nextPop = staggerIdx !== undefined
        ? (8 + staggerIdx * (24 / COUNT) + Math.random() * 8)
        : (t + 12 + Math.random() * 24);
      return {
        baseX:   (-halfW + Math.random() * halfW * 2) * 0.88,
        startY:  -halfH + Math.random() * halfH * 2,
        z:       Z_PLANE - 0.35 + Math.random() * 0.7,
        speed:   0.035 + size * 0.12 + Math.random() * 0.03,
        swA:     0.12  + Math.random() * 0.3,
        swF:     0.15  + Math.random() * 0.3,
        ph:      Math.random() * 6.28,
        sp:      0.6   + Math.random() * 1.0,
        size,
        wob:     0.04  + Math.random() * 0.05,
        nextPop,
        popStart: -1,
        bornAt:   0,
      };
    }

    const meshes = [];
    const _bx = [], _by = [], _br = [], _ox = [], _oy = [];
    for (let i = 0; i < COUNT; i++) {
      const m = new THREE.Mesh(bubbleGeo, bubbleMat);
      m.userData = spawnData(0, i);
      m.position.set(m.userData.baseX, m.userData.startY, m.userData.z);
      m.scale.setScalar(0);
      m.renderOrder = 10;
      group.add(m);
      meshes.push(m);
      _ox.push(0); _oy.push(0);
    }

    let running = true;

    function tick(ts) {
      if (!running) return;
      requestAnimationFrame(tick);
      const t = ts * 0.001;
      bubbleMat.uniforms.uTime.value = t;

      const { halfH } = worldBounds();
      const span = halfH * 2 + 0.5;

      // Pass 1 : position + taille
      for (let i = 0; i < meshes.length; i++) {
        const b = meshes[i], u = b.userData;

        let yy = u.startY - t * u.speed;
        yy = yy - Math.floor((yy + halfH + 0.25) / span) * span;

        let xx = u.baseX + Math.sin(t * u.swF + u.ph) * u.swA;
        let s  = u.size;

        if (u.popStart < 0 && u.bornAt < 0 && t > u.nextPop) u.popStart = t;

        if (u.popStart >= 0) {
          const k = (t - u.popStart) / 1.7;
          if (k >= 1) {
            u.popStart = -1;
            Object.assign(u, spawnData(t));
            s = 0;
          } else {
            s = u.size * (1 - k * k * (3 - 2 * k));
          }
        }

        if (u.bornAt >= 0) {
          const k = (t - u.bornAt) / 1.3;
          if (k >= 1) { u.bornAt = -1; }
          else { s *= k * k * (3 - 2 * k); }
        }

        _bx[i] = xx; _by[i] = yy; _br[i] = s;
      }

      // Pass 2 : répulsion douce entre bulles
      const tox = meshes.map(() => 0), toy = meshes.map(() => 0);
      for (let i = 0; i < meshes.length; i++) {
        if (_br[i] < 0.02) continue;
        for (let j = i + 1; j < meshes.length; j++) {
          if (_br[j] < 0.02) continue;
          const dx  = (_bx[i] + _ox[i]) - (_bx[j] + _ox[j]);
          const dy  = (_by[i] + _oy[i]) - (_by[j] + _oy[j]);
          const min = (_br[i] + _br[j]) * 1.04;
          const d2  = dx * dx + dy * dy;
          if (d2 < min * min && d2 > 1e-6) {
            const d = Math.sqrt(d2), ov = (min - d) * 0.5;
            tox[i] += (dx / d) * ov; toy[i] += (dy / d) * ov;
            tox[j] -= (dx / d) * ov; toy[j] -= (dy / d) * ov;
          }
        }
      }

      // Apply
      for (let i = 0; i < meshes.length; i++) {
        const b = meshes[i], u = b.userData;
        _ox[i] += (tox[i] - _ox[i]) * 0.22;
        _oy[i] += (toy[i] - _oy[i]) * 0.22;
        b.position.set(_bx[i] + _ox[i], _by[i] + _oy[i], u.z);
        const w = Math.sin(t * (1.4 + u.sp) + u.ph) * u.wob;
        const s = _br[i];
        b.scale.set(s * (1 + w), s * (1 - w), s);
        b.rotation.y += 0.0008 * u.sp;
        b.rotation.x  = Math.sin(t * 0.1 + u.ph) * 0.08;
      }

      renderer.render(scene, camera);
    }

    requestAnimationFrame(tick);

    return {
      setAccent(hex) { bubbleMat.uniforms.uTint.value.set(hex); },
      setOpacity(v)  { bubbleMat.uniforms.uOpacity.value = Math.max(0, Math.min(1, v)); },
      destroy() {
        running = false;
        renderer.dispose();
        window.removeEventListener('resize', resize);
      },
    };
  }

  window.BubblesFX = { create: createBubbles };
})();
